import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { apiInstance } from '@/utils';

/** Current plan keys (post 2026-06-14 rename). Legacy aliases retained for backward compat. */
export type PaidTierPlan = 'prospect' | 'outreach' | 'growth' | 'starter' | 'standard' | 'pro';

// Get current subscription for a workspace
export const getCurrentSubscription = async () => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.currentSubscription,
  });
  return result.data;
};

// Get subscription history
export const getSubscriptionHistory = async () => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.subscriptionHistory,
  });
  return result.data;
};

// Create a new subscription checkout (trial or paid tier).
export const createSubscription = async (data: {
  priceId?: string;
  planName?: string;
  term?: string;
  plan?: PaidTierPlan;
  is_trial?: boolean;
  product_module?: 'sales' | 'marketing';
}): Promise<{ sessionId: string; checkoutUrl: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.createSubscription,
  });
  return result.data.data;
};

// Get per-action enrichment usage for the last 30 days (Fiverr cost table)
export const getEnrichmentUsage = async (): Promise<
  { action_type: string; call_count: number; credits_consumed: number }[]
> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.enrichmentUsage,
  });
  return result.data.data;
};

// Update auto top-up settings (enabled, threshold, package)
export const updateAutoTopup = async (settings: {
  enabled?: boolean;
  threshold?: number;
  package?: 'starter' | 'growth' | 'pro';
}): Promise<void> => {
  await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data: settings,
    url: API_ENDPOINTS.autoTopup,
  });
};

// Update AI credit auto-recharge settings (enabled, threshold, package)
export const updateAiAutoTopup = async (settings: {
  enabled?: boolean;
  threshold?: number;
  package?: 'small' | 'medium' | 'large';
}): Promise<void> => {
  await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data: settings,
    url: API_ENDPOINTS.aiAutoTopup,
  });
};

/** Flat workspace tier checkout — no seat quantity. */
export const createPaidTierSubscription = async (
  plan: PaidTierPlan = 'outreach',
  options?: {
    product_module?: 'sales' | 'marketing';
    onboarding_checkout?: boolean;
  },
): Promise<{ sessionId: string; checkoutUrl: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: {
      is_trial: false,
      plan,
      ...(options?.product_module !== undefined && { product_module: options.product_module }),
      ...(options?.onboarding_checkout === true && { onboarding_checkout: true }),
    },
    url: API_ENDPOINTS.createSubscription,
  });
  return result.data.data;
};

/** @deprecated use createPaidTierSubscription */
export const createStandardSubscription = async (
  _seats?: number,
  options?: {
    product_module?: 'sales' | 'marketing';
    onboarding_checkout?: boolean;
  },
): Promise<{ sessionId: string; checkoutUrl: string }> =>
  createPaidTierSubscription('standard', options);

// Create a Stripe checkout session for enrichment credit topup
export const createCreditTopup = async (
  packageName: 'starter' | 'growth' | 'pro',
): Promise<{ sessionId: string; checkoutUrl: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { package: packageName },
    url: API_ENDPOINTS.creditTopup,
  });
  return result.data.data;
};

// Create a Stripe checkout session for AI credit topup
export const createAiCreditTopup = async (
  packageName: 'small' | 'medium' | 'large',
): Promise<{ sessionId: string; checkoutUrl: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { package: packageName },
    url: API_ENDPOINTS.aiCreditTopup,
  });
  return result.data.data;
};

// Create a Stripe checkout session for video avatar credit topup
export const createAvatarCreditTopup = async (
  packageName: 'starter' | 'growth' | 'pro',
): Promise<{ sessionId: string; checkoutUrl: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { package: packageName },
    url: API_ENDPOINTS.avatarCreditTopup,
  });
  return result.data.data;
};

export const updateAvatarAutoTopup = async (settings: {
  enabled?: boolean;
  threshold?: number;
  package?: 'starter' | 'growth' | 'pro';
}): Promise<void> => {
  await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data: settings,
    url: API_ENDPOINTS.avatarAutoTopup,
  });
};

export const getModuleSubscriptions = async (): Promise<{
  success: boolean;
  data: { sales: object | null; marketing: object | null };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.moduleSubscriptions,
  });
  return result.data;
};

export const cancelSubscription = async (productModule?: 'sales' | 'marketing'): Promise<{
  success: boolean;
  message: string;
  current_period_end?: string;
  trial_cancelled?: boolean;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.cancelSubscription,
    data: productModule ? { product_module: productModule } : {},
  });
  return result.data;
};

export const reactivateSubscription = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.reactivateSubscription,
  });
  return result.data;
};

export const changePlan = async (
  plan: PaidTierPlan,
): Promise<{ success: boolean; plan: PaidTierPlan; monthly_price_cents: number; is_upgrade: boolean; message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { plan },
    url: API_ENDPOINTS.planChange,
  });
  return result.data;
};

export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.billingPortal,
  });
  return result.data;
};
